import { useState } from 'react';
import { Facebook, Instagram, AtSign, Youtube, Music2 } from 'lucide-react';
import type { InsightsSnapshot, SocialPlatform } from '@unitly/contracts';
import { useSocialInsights } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { PageLoader, EmptyState } from '../../ui/primitives.tsx';
import { WorkspaceChannelsPanel } from './WorkspaceChannels.tsx';
import { relativeTime } from '../../lib/dates.ts';

const PLATFORM_ICON: Record<SocialPlatform, typeof Facebook> = {
  facebook: Facebook, instagram: Instagram, threads: AtSign, youtube: Youtube, tiktok: Music2,
};
const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads', youtube: 'YouTube', tiktok: 'TikTok',
};

function agoLabel(iso: string) {
  const rel = relativeTime(iso);
  if (rel === 'ora') return 'adesso';
  return /min|h$/.test(rel) ? `${rel} fa` : rel;
}

function Breakdown({ title, data }: { title: string; data: Record<string, number> | null }) {
  if (!data) return null;
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6);
  return (
    <div>
      <div className="hint" style={{ padding: 0, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12.5, width: 70, flex: 'none', color: 'var(--label-2)' }}>{k}</span>
            <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--fill)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.round((v / total) * 100)}%`, height: '100%', background: 'var(--blue)' }} />
            </div>
            <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--label-3)', width: 34, textAlign: 'right' }}>
              {Math.round((v / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Slide({ s }: { s: InsightsSnapshot }) {
  const Icon = PLATFORM_ICON[s.platform];
  return (
    <div className="card card-pad insights-slide">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span className="connector-logo" style={{ width: 36, height: 36 }}><Icon size={18} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{s.pageName}</div>
          <div className="hint" style={{ padding: 0 }}>Aggiornato {agoLabel(s.fetchedAt)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s.followers != null ? s.followers.toLocaleString('it-IT') : '—'}</div>
          <div className="hint" style={{ padding: 0 }}>{s.platform === 'youtube' ? 'Iscritti' : 'Follower'}</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s.engagementRate != null ? `${s.engagementRate}%` : '—'}</div>
          <div className="hint" style={{ padding: 0 }}>Engagement medio</div>
        </div>
      </div>
      {s.stats28d.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="hint" style={{ padding: 0, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>Ultimi 28 giorni</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
            {s.stats28d.map((st) => (
              <div key={st.label}>
                <div style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{st.value.toLocaleString('it-IT')}</div>
                <div className="hint" style={{ padding: 0 }}>{st.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(s.audienceAge || s.audienceGender || s.audienceLocations) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
          <Breakdown title="Età" data={s.audienceAge} />
          <Breakdown title="Genere" data={s.audienceGender} />
          <Breakdown title="Luoghi" data={s.audienceLocations} />
        </div>
      )}
      {s.topContent.length > 0 && (
        <div>
          <div className="hint" style={{ padding: 0, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>Ultimi contenuti</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {s.topContent.slice(0, 3).map((c) => (
              <a key={c.id} href={c.permalink ?? undefined} target="_blank" rel="noreferrer"
                style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                {c.thumbnailUrl && <img src={c.thumbnailUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flex: 'none' }} />}
                <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.caption || '(senza testo)'}</span>
                {c.metrics.likes != null && <span className="hint" style={{ padding: 0, flex: 'none' }}>❤ {c.metrics.likes}</span>}
              </a>
            ))}
          </div>
        </div>
      )}
      {s.unavailable.length > 0 && (
        <div className="hint" style={{ padding: 0, marginTop: 14, color: 'var(--label-3)' }}>
          {s.unavailable.join(' · ')}
        </div>
      )}
    </div>
  );
}

export function SocialInsightsView({ workspaceId, canManage }: { workspaceId: string; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const insights = useSocialInsights(orgId, workspaceId);
  const [platform, setPlatform] = useState<SocialPlatform | 'all'>('all');
  const platforms = [...new Set((insights.data ?? []).map((s) => s.platform))];
  const slides = (insights.data ?? []).filter((s) => platform === 'all' || s.platform === platform);

  return (
    <>
      <WorkspaceChannelsPanel workspaceId={workspaceId} canManage={canManage} />
      {insights.isLoading ? <PageLoader /> : !insights.data?.length ? (
        <div className="card"><EmptyState tone="neutral" title="Nessun dato">Assegna a questo spazio un account Facebook, Instagram, Threads, YouTube o TikTok per vedere le statistiche.</EmptyState></div>
      ) : (
        <>
          {/* The carousel only scrolls sideways (swipe on phone): on desktop these are the way to reach each account. */}
          {platforms.length > 1 && (
            <div className="chips" role="tablist" aria-label="Piattaforma" style={{ marginBottom: 12 }}>
              <button type="button" role="tab" className="chip" aria-pressed={platform === 'all'} onClick={() => setPlatform('all')}>Tutte</button>
              {platforms.map((p) => (
                <button key={p} type="button" role="tab" className="chip" aria-pressed={platform === p} onClick={() => setPlatform(p)}>{PLATFORM_LABEL[p]}</button>
              ))}
            </div>
          )}
          <div className="insights-carousel">
            {slides.map((s) => <Slide key={`${s.connectionId}:${s.pageId ?? s.connectorKey}`} s={s} />)}
          </div>
        </>
      )}
    </>
  );
}
